import { Box, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';

import Tooltip from 'components/tooltip';
import { useAccountState } from 'contexts/account/account-context';
import dayjs from 'dayjs';
import { useGet } from 'hooks/use-get';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Mui from 'materials';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import _ from 'underscore';
import { loadState, saveState } from 'utils/storage';
import { getPonds, getZones } from 'views/production-systems/api/productionSystems';
import SelectFarm from 'views/production-systems/components/select-farm';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import ListPondsActions from 'views/production-systems/features/farm-overview/components/list-ponds-actions';
import ListPondsFilter from 'views/production-systems/features/farm-overview/components/list-ponds-filters';
import ListPondsList from 'views/production-systems/features/farm-overview/components/list-ponds-list';
import { ListPondsFilters } from 'views/production-systems/types/list-ponds-filters.type';
import { Pond, PondStatus, PondType, RegistrationDateType } from 'views/production-systems/types/pond.type';
import { calculateBiomass } from 'views/production-systems/utils/farm-overview';
import ProductionSystemsTabHeader from '../../../components/tab-header';
import Paper from 'components/paper';
import Checkbox from 'components/checkbox';

type ListPondProps = {};

type ParamTypes = {
  globalSiteId: string;
};

const DateButtonArrow = styled(Mui.Button)(({ theme }) => ({
  height: 30,
  minHeight: 30,
  borderRadius: 8,
  color: theme.palette.darkBlue.main,
  background: theme.palette.common.white,
  '&:active': {
    color: theme.palette.darkBlue.main,
    background: theme.palette.common.white,
  },
  '&:focus': {
    color: theme.palette.darkBlue.main,
    background: theme.palette.common.white,
  },
  '&:hover': {
    color: theme.palette.darkBlue.main,
    background: theme.palette.common.white,
  },
}));

const PAGE_NAME_ID = 'productionSystems.farmOverview';

type SortablePond = Pond & {
  biomass: number;
  status: boolean;
};

const defaultValues: ListPondsFilters = {
  zoneId: '',
  searchValue: '',
  showActivePonds: true,
  showInactivePonds: true,
  selectedPondTypes: Object.values(PondType),
  enableKg: true,
};

const ListPonds: React.FC<ListPondProps> = ({}) => {
  const intl = useIntl();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { globalSiteId, changeGlobalSiteId, registrationDate, changeRegistrationDate, selectedPonds, setSelectedPonds } =
    usePondsState();

  const { account } = useAccountState();
  const params = useParams<ParamTypes>();

  // #region STATES
  const [pondList, setPondList] = useState<SortablePond[]>([]);
  const [sourcePonds, setSourcePonds] = useState<SortablePond[]>([]);
  const [filters, setFilters] = useState<ListPondsFilters>(loadState({ key: 'pondFilters' }) || defaultValues);

  // #region FETCH DATA

  const { data: pondState, isFetching, isLoading: loadingPonds } = useGet(
    ['getPonds', globalSiteId, registrationDate.format('YYYY-MM-DD')],
    ({ queryKey }) => getPonds(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!globalSiteId,
    }
  );

  useEffect(() => {
    if (!pondState) return;

    let ponds: SortablePond[] = _.chain(pondState.ponds)
      .map((pond) => ({
        ...pond,
        status: pond.pondStatus === PondStatus.active ? true : false,
        biomass: calculateBiomass(pond.currentMw, pond.currentQty),
      }))
      .sortBy('pondName')
      .value();

    setSourcePonds(ponds);
  }, [pondState]);

  const { data: zones } = useGet(['getZones', globalSiteId], ({ queryKey }) => getZones(String(queryKey[1])), {
    select: (data) =>
      data.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
          numeric: true,
        })
      ),
    enabled: !!globalSiteId,
  });

  // HANDLERS

  const updatePondOnFilterSelection = (filters: ListPondsFilters) => {
    const filteredSelectedPonds = sourcePonds
      .filter((p) => selectedPonds.includes(p.pondId))
      .filter((p) => (filters.showActivePonds ? true : p.pondStatus != PondStatus.active))
      .filter((p) => (filters.showInactivePonds ? true : p.pondStatus != PondStatus.inactive))
      .map((p) => p.pondId);

    setSelectedPonds(filteredSelectedPonds);
  };

  const handleSelectedPonds = (value: Pond, checked: boolean) => {
    setSelectedPonds(checked ? selectedPonds.concat([value.pondId]) : selectedPonds.filter((s) => s !== value.pondId));
  };

  const addDate = () => changeRegistrationDate(registrationDate.add(1, 'days'), RegistrationDateType.overview);

  const subtractDate = () => changeRegistrationDate(registrationDate.subtract(1, 'days'), RegistrationDateType.overview);

  // #endregion FETCH DATA

  // #region HELPERS

  const resetStates = () => {
    updateFilters(defaultValues);
  };

  const updateFilters = (newFilters: ListPondsFilters) => {
    setFilters(newFilters);
  };

  // #endregion HELPERS

  // #region HOOKS

  useEffect(() => {
    saveState({ key: 'pondFilters', state: filters });
  }, [filters]);

  useEffect(() => {
    if (account.company?.addressAccess.length === 1) {
      history.push(`/production-systems/farm-overview/${String(account.company?.addressAccess[0].id)}`);
    }
  }, [account.company?.addressAccess, history]);

  useEffect(() => {
    if (params.globalSiteId) {
      changeGlobalSiteId(params.globalSiteId);
    } else {
      changeGlobalSiteId('');
      resetStates();
    }
  }, [params.globalSiteId]);

  useEffect(() => {
    updatePondOnFilterSelection(filters);
  }, [filters]);

  useEffect(() => {
    const rows = _.chain(sourcePonds)
      .filter((pond) => (filters.showInactivePonds ? true : pond.pondStatus === PondStatus.active))
      .filter((pond) => (filters.showActivePonds ? true : pond.pondStatus === PondStatus.inactive))
      .filter((pond) => (!filters.zoneId ? true : pond.zoneId === filters.zoneId))
      .filter((pond) => (!filters.selectedPondTypes ? true : filters.selectedPondTypes.includes(pond.pondType)))
      .filter((pond) => pond.pondName.toLowerCase().includes(filters.searchValue.toLowerCase()))
      .value();
    setPondList(rows);
  }, [filters, sourcePonds]);

  // #endregion HOOKS

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <ProductionSystemsTabHeader
        utils={
          <Breadcrumbs
            crumbs={
              !!params.globalSiteId
                ? [
                    {
                      url: params.globalSiteId,
                      text: account.company?.addressAccess.find((a) => a.id === params.globalSiteId)?.name || '',
                    },
                  ]
                : undefined
            }
          />
        }
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
        <SelectFarm
          selectedId={!!params.globalSiteId ? params.globalSiteId : ''}
          onChange={resetStates}
          uri="/production-systems/farm-overview"
        />
      </Box>
      {!!params.globalSiteId ? (
        <Paper transparent>
          <Box display={'flex'} flexDirection={'column'} gap={4}>
            <ListPondsFilter
              renderPonds={pondList}
              filters={filters}
              updateFilters={updateFilters}
              clearFilters={resetStates}
              zones={zones}
            />
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'space-between'}
              color={theme.palette.common.white}
              flexDirection={isMobile ? 'column-reverse' : 'row'}>
              <Box display={'flex'} alignItems={'center'} justifyContent={'flex-start'} width={200}>
                <Checkbox
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.selectAll` })}
                  checked={selectedPonds.length === pondList.length}
                  onChange={() => {
                    selectedPonds.length === pondList.length
                      ? setSelectedPonds([])
                      : setSelectedPonds(pondList.map((pond) => pond.pondId));
                  }}
                />
              </Box>
              <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} gap={4} color={theme.palette.common.white}>
                <Typography>
                  <FormattedMessage id={`${PAGE_NAME_ID}.filters.registrationDate`} />
                  {': '}
                  <span>{dayjs(registrationDate).format('DD/MM/YYYY')}</span>
                </Typography>

                <Tooltip title={intl.formatMessage({ id: `${PAGE_NAME_ID}.buttons.previousDay` })}>
                  <DateButtonArrow
                    disableRipple
                    onClick={subtractDate}
                    sx={{
                      marginRight: 2,
                    }}>
                    <ChevronLeft size={18} />
                  </DateButtonArrow>
                </Tooltip>
                <Tooltip title={intl.formatMessage({ id: `${PAGE_NAME_ID}.buttons.nextDay` })}>
                  <DateButtonArrow disableRipple disabled={registrationDate.isSameOrAfter(dayjs(), 'day')} onClick={addDate}>
                    <ChevronRight size={18} />
                  </DateButtonArrow>
                </Tooltip>
              </Box>
            </Box>
            <Box overflow={'auto'}>
              <ListPondsList
                renderPonds={pondList}
                setPonds={setPondList}
                isFetching={isFetching}
                handleSelectedPonds={handleSelectedPonds}
                isKg={filters.enableKg}
              />
            </Box>
            <ListPondsActions renderPonds={pondList} ponds={sourcePonds} loadingPonds={loadingPonds} />
          </Box>
        </Paper>
      ) : null}
    </Box>
  );
};

export default ListPonds;
